import { zodResolver } from "@hookform/resolvers/zod";
import type { ComponentProps } from "react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { z } from "zod";
import { ArrowRight, Eye, EyeOff, Loader2, Mail } from "lucide-react";

import {
  getLandingPrivacyUrl,
  getLandingTermsUrl,
} from "@/lib/landingLinks";
import { cn } from "@/lib/utils";

import { AuthBrandColumn } from "@/components/auth/AuthBrandColumn";
import { AuthBrandMark } from "@/components/auth/AuthBrandMark";
import { GoogleSsoButton } from "@/components/auth/GoogleSsoButton";
import { useAuth } from "@/contexts/AuthContext";

type LoginMode = "magic_link" | "password";

export function LoginForm({
  className,
  returnTo = "/",
  ...props
}: ComponentProps<"div"> & { returnTo?: string }): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const privacyUrl = getLandingPrivacyUrl();
  const termsUrl = getLandingTermsUrl();
  const { requestLoginLink, loginWithPassword } = useAuth();
  const [mode, setMode] = useState<LoginMode>("magic_link");
  const [showPassword, setShowPassword] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");

  const loginSchema = useMemo(
    () =>
      z.object({
        email: z.string().min(1, t("validation.emailRequired")).email(t("validation.emailInvalid")),
        password: z.string().optional(),
      }),
    [t]
  );
  type LoginFormValues = z.infer<typeof loginSchema>;

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const password = watch("password");

  const onSubmit = async (values: LoginFormValues): Promise<void> => {
    if (mode === "password") {
      const p = values.password?.trim();
      if (!p) {
        setError("password", {
          message: t("validation.required", "This field is required."),
          type: "required",
        });
        return;
      }
    }
    try {
      if (mode === "password" && values.password) {
        await loginWithPassword(values.email, values.password.trim());
        navigate(returnTo, { replace: true });
        return;
      }
      await requestLoginLink(values.email, locale);
      setSentEmail(values.email);
      setSent(true);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message =
        mode === "password"
          ? t("auth.login.invalidCredentials", "Invalid email or password.")
          : status === 404
            ? t("auth.noAccountForEmail", "No account found for this email. Please register first.")
            : t("auth.login.error", "Something went wrong. Try again.");
      toast.error(message);
    }
  };

  const handleResend = (): void => {
    setSent(false);
  };

  // Magic-link sent — same shell, simplified left column.
  if (sent) {
    return (
      <div className={cn("fx-auth-card", className)} {...props}>
        <div className="fx-auth-form-col">
          <div className="fx-auth-form-inner">
            <AuthBrandMark />
            <span className="fx-auth-eyebrow">
              {t("auth.login.checkEmailEyebrow", "Revisá tu correo")}
            </span>
            <h1 className="fx-auth-h1">{t("auth.login.checkEmail")}</h1>
            <p className="fx-auth-lead">
              {t("auth.login.checkEmailInstructions", {
                email: sentEmail,
                defaultValue: `Te enviamos un enlace a ${sentEmail}. Hacé clic en el enlace para entrar.`,
              })}
            </p>
            <button
              type="button"
              className="fx-auth-btn fx-auth-btn-ghost"
              onClick={handleResend}
            >
              {t("auth.login.backToLogin", "Volver")}
            </button>
            <div className="fx-auth-foot">
              <Trans
                i18nKey="auth.login.legalAgreement"
                components={{
                  terms: (
                    <a href={termsUrl} target="_blank" rel="noopener noreferrer" />
                  ),
                  privacy: (
                    <a href={privacyUrl} target="_blank" rel="noopener noreferrer" />
                  ),
                }}
              />
            </div>
          </div>
        </div>
        <AuthBrandColumn mode="login" />
      </div>
    );
  }

  return (
    <div className={cn("fx-auth-card", className)} {...props}>
      <div className="fx-auth-form-col">
        <div className="fx-auth-form-inner">
        <AuthBrandMark />
        <h1 className="fx-auth-h1">
          {t("auth.login.titleLead", "Iniciá sesión")}{" "}
          <span className="ital">
            {t("auth.login.titleAccent", "en Fiscalo.")}
          </span>
        </h1>
        <p className="fx-auth-lead">
          {mode === "password"
            ? t(
                "auth.login.subtitlePassword",
                "Ingresá tu correo y contraseña para entrar.",
              )
            : t(
                "auth.login.subtitleMagic",
                "Ingresá tu correo y te enviamos un enlace para entrar sin contraseña.",
              )}
        </p>

        <div className="fx-auth-form">
          <GoogleSsoButton returnTo={returnTo !== "/" ? returnTo : undefined} />
          <div className="fx-auth-divider">{t("auth.login.divider", "o bien")}</div>
        </div>

        <form className="fx-auth-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="fx-auth-field">
            <label htmlFor="email">{t("auth.login.email")}</label>
            <input
              id="email"
              type="email"
              placeholder="vos@email.com"
              autoComplete="email"
              {...register("email")}
            />
            {errors.email ? (
              <p className="fx-auth-error">{errors.email.message}</p>
            ) : null}
          </div>

          {mode === "password" && (
            <div className="fx-auth-field">
              <label htmlFor="password">{t("auth.login.password")}</label>
              <div className="fx-auth-field-wrap">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  {...register("password")}
                />
                <button
                  type="button"
                  className="show"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={
                    showPassword
                      ? t("auth.login.hidePassword", "Ocultar contraseña")
                      : t("auth.login.showPassword", "Mostrar contraseña")
                  }
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
              {errors.password ? (
                <p className="fx-auth-error">{errors.password.message}</p>
              ) : null}
              <div style={{ textAlign: "right", marginTop: 2 }}>
                <Link
                  to="/auth/forgot-password"
                  style={{
                    fontSize: 12.5,
                    color: "var(--fx-ink-500)",
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                  }}
                >
                  {t("auth.login.forgotPassword", "¿Olvidaste tu contraseña?")}
                </Link>
              </div>
            </div>
          )}

          <button
            type="submit"
            className="fx-auth-btn fx-auth-btn-primary"
            disabled={isSubmitting || (mode === "password" && !password)}
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : mode === "magic_link" ? (
              <>
                <Mail aria-hidden />
                {t("auth.login.submit", "Enviar enlace de acceso")}
              </>
            ) : (
              <>
                {t("auth.login.signIn", "Iniciar sesión")}
                <ArrowRight aria-hidden />
              </>
            )}
          </button>

          <div className="fx-auth-divider">{t("auth.login.divider", "o bien")}</div>

          <button
            type="button"
            className="fx-auth-btn fx-auth-btn-ghost"
            onClick={() =>
              setMode(mode === "magic_link" ? "password" : "magic_link")
            }
          >
            {mode === "magic_link"
              ? t("auth.login.signInWithPassword", "Iniciar sesión con contraseña")
              : t("auth.login.sendMagicLinkInstead", "Usar enlace mágico")}
          </button>

          <div className="fx-auth-alt" style={{ marginTop: 8 }}>
            {t("auth.login.noAccountQuestion", "¿Todavía no tenés cuenta?")}{" "}
            <Link to="/register">
              {t("auth.login.createNewAccount", "Creá una gratis")}
              <ArrowRight aria-hidden />
            </Link>
          </div>
        </form>

        <div className="fx-auth-foot">
          <Trans
            i18nKey="auth.login.legalAgreement"
            components={{
              terms: (
                <a href={termsUrl} target="_blank" rel="noopener noreferrer" />
              ),
              privacy: (
                <a href={privacyUrl} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          />
        </div>
        </div>
      </div>

      <AuthBrandColumn mode="login" />
    </div>
  );
}
